/**
 * Verschieben, Rueckgaengig und die Frage „wo steckt das?“ (ADR 0013, ADR 0014).
 *
 * **Einzelne Dinge werden verschoben, Behaelter werden befuellt.** Ob ein
 * Artikel auf der Artikel-Seite verschoben, in eine Kiste eingebucht oder aus
 * ihr ausgebucht wird -- es ist immer dieselbe Bewegung zu einem Ziel. Welche
 * Buchungsart daraus wird, entscheidet diese Datei, damit Oberflaeche und
 * spaeter der Mehrfachscan und „Alles da“ dieselbe Regel benutzen.
 *
 * Rein und ohne Datenbank, wie alles in `@thw/core-domain`: Das Geraet rechnet
 * damit offline.
 */
import { MAX_PACKSTUECK_TIEFE } from "./packstueck.js";
/**
 * Die Buchung, die einen Artikel an ein Ziel bringt.
 *
 * - An eine **Einsatzstelle**: `einsatz`.
 * - In ein **Packstueck, das draussen ist**: Der Artikel teilt dessen
 *   Verbleib. Ist die Kiste im Einsatz, bucht das `einsatz` mit derselben
 *   Einsatzstelle; ist sie verliehen, `ausgabe`. Das Ziel ist die Kiste
 *   selbst -- wie bei den abgeleiteten Buchungen (ADR 0012). Kommt die Kiste
 *   zurueck, kommt der Artikel mit.
 * - Sonst auf einen Lagerort: `umlagerung`, wenn der Artikel schon im Lager
 *   ist, `einbuchen`, wenn er von draussen kommt oder noch nie erfasst war
 *   (KONZEPT.md §7.1).
 *
 * `behaelter` ist die Lage des Packstuecks, wenn das Ziel eines ist, sonst
 * `null`. Der Status aendert sich nie (ADR 0005).
 */
export function verschiebenNach(vorher, ziel, behaelter) {
    if (ziel.art === "einsatzstelle") {
        return { art: "einsatz", ziel_lagerort_id: null, einsatzstelle_id: ziel.id, status: null };
    }
    if (behaelter?.verbleib === "im_einsatz") {
        return {
            art: "einsatz",
            ziel_lagerort_id: ziel.id,
            einsatzstelle_id: behaelter.einsatzstelle_id,
            status: null,
        };
    }
    if (behaelter?.verbleib === "ausgeliehen") {
        return { art: "ausgabe", ziel_lagerort_id: ziel.id, einsatzstelle_id: null, status: null };
    }
    return {
        art: vorher.verbleib === "im_lager" ? "umlagerung" : "einbuchen",
        ziel_lagerort_id: ziel.id,
        einsatzstelle_id: null,
        status: null,
    };
}
/** Kommentar der Gegenbuchungen -- so steht es in der Historie. */
export const RUECKGAENGIG_KOMMENTAR = "Rückgängig";
function gleicheLage(a, b) {
    return (a.verbleib === b.verbleib &&
        a.lagerort_id === b.lagerort_id &&
        a.einsatzstelle_id === b.einsatzstelle_id);
}
/**
 * Die Gegenbuchungen, die `jetzt` wieder zu `vorher` machen (ADR 0013 §15).
 *
 * Das Log bleibt unveraendert (CLAUDE.md §3.2). Zurueck geht es mit
 * gewoehnlichen Bewegungen, nicht mit `korrektur`: Nur eine Bewegung nimmt den
 * Inhalt eines Packstuecks mit, und nur sie fuehrt zurueck in den Einsatz.
 *
 * - **Im Lager**: zurueck an den Lagerort.
 * - **Im Einsatz**: `einsatz` an dieselbe Einsatzstelle, ins selbe Packstueck.
 * - **Verliehen**: `ausgabe`, und der Artikel ist wieder verliehen (so vom
 *   Nutzer festgelegt, ADR 0014).
 * - **Noch nie erfasst**, oder im Lager ohne bekannten Ort: an das
 *   Standardziel „Ohne festen Platz“. Zurueck in „nicht erfasst“ fuehrt keine
 *   Buchung, und dort waere er ohnehin gewesen (ADR 0014). Ohne Standardziel
 *   bleibt die Lage, wie sie ist.
 * - **Status**: War er vorher ein anderer -- durch eine Statusaenderung, oder
 *   weil ein verlorener Artikel beim Bewegen wieder auftauchte --, folgt eine
 *   Statusaenderung zurueck. Sie steht immer nach der Bewegung.
 *
 * Leer, wenn nichts zurueckzunehmen ist.
 */
export function rueckgaengig(vorher, jetzt, standardziel) {
    const ergebnis = [];
    if (!gleicheLage(vorher, jetzt)) {
        const zurueckIns = (ziel_lagerort_id) => ({
            art: jetzt.verbleib === "im_lager" ? "umlagerung" : "einbuchen",
            ziel_lagerort_id,
            einsatzstelle_id: null,
            status: null,
        });
        switch (vorher.verbleib) {
            case "im_lager":
            case "nicht_erfasst": {
                const ort = vorher.verbleib === "im_lager" ? (vorher.lagerort_id ?? standardziel) : standardziel;
                // Schon dort, wo er hin soll (etwa am Standardziel)? Dann nichts.
                if (ort !== null && !(jetzt.verbleib === "im_lager" && jetzt.lagerort_id === ort)) {
                    ergebnis.push(zurueckIns(ort));
                }
                break;
            }
            case "im_einsatz":
                ergebnis.push({
                    art: "einsatz",
                    ziel_lagerort_id: vorher.lagerort_id,
                    einsatzstelle_id: vorher.einsatzstelle_id,
                    status: null,
                });
                break;
            case "ausgeliehen":
                ergebnis.push({
                    art: "ausgabe",
                    ziel_lagerort_id: vorher.lagerort_id,
                    einsatzstelle_id: null,
                    status: null,
                });
                break;
        }
    }
    if (vorher.status !== jetzt.status) {
        ergebnis.push({
            art: "statusaenderung",
            ziel_lagerort_id: null,
            einsatzstelle_id: null,
            status: vorher.status,
        });
    }
    return ergebnis;
}
/**
 * Der Ort, in dem ein Lagerort gerade steckt.
 *
 * Feste Orte haengen, wie sie in den Stammdaten angelegt sind (`eltern_id`).
 * Ein **Packstueck mit Artikel** steckt dort, wo sein Artikel gebucht ist
 * (ADR 0013 §13). Wurde er nie gebucht, gelten die Stammdaten. Ist die Kiste
 * draussen und in keinem anderen Packstueck, steckt sie nirgends (`null`).
 */
export function uebergeordneterOrt(ort, lageVon) {
    if (ort.artikel_id !== null) {
        const lage = lageVon(ort.artikel_id);
        if (lage !== undefined && lage.verbleib !== "nicht_erfasst")
            return lage.lagerort_id;
    }
    return ort.eltern_id;
}
/**
 * Der Weg von einem Lagerort nach oben: er selbst, dann was ihn enthaelt, und
 * so weiter. Bricht ab bei einem Kreis -- den es im Log nach zwei widerstreitenden
 * Offline-Buchungen geben kann -- und nach einer festen Tiefe.
 */
export function ortsKette(start, orte, lageVon) {
    const kette = [];
    let aktuell = start;
    // Stammdaten koennen tiefer sein als Kisten in Kisten -- Halle, Regal, Fach.
    const grenze = MAX_PACKSTUECK_TIEFE * 3;
    while (aktuell !== null && !kette.includes(aktuell) && kette.length < grenze) {
        kette.push(aktuell);
        const ort = orte.get(aktuell);
        aktuell = ort === undefined ? null : uebergeordneterOrt(ort, lageVon);
    }
    return kette;
}
/**
 * Darf dieser Artikel nicht an dieses Ziel?
 *
 * Ein Packstueck laesst sich nicht in sich selbst stecken und nicht in eines
 * seiner Unter-Packstuecke (ADR 0013 §4). Fuer alle anderen Artikel und fuer
 * Einsatzstellen als Ziel gibt es kein Hindernis.
 */
export function zielVerboten(artikel_id, ziel, orte, lageVon) {
    if (ziel.art !== "lagerort")
        return false;
    let eigenerOrt = null;
    for (const o of orte.values()) {
        if (o.artikel_id === artikel_id) {
            eigenerOrt = o.id;
            break;
        }
    }
    if (eigenerOrt === null)
        return false;
    return ortsKette(ziel.id, orte, lageVon).includes(eigenerOrt);
}
//# sourceMappingURL=verschieben.js.map